import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { X, GripVertical, Mail, Phone, Briefcase, Scale } from "lucide-react";

const STAGES = [
  { id: "filed", label: "FILED", accent: "amber-400", desc: "Fresh inquiries" },
  { id: "retained", label: "RETAINED", accent: "emerald-400", desc: "Payment captured" },
  { id: "in_trace", label: "IN TRACE", accent: "sky-400", desc: "Active forensic work" },
  { id: "delivered", label: "DELIVERED", accent: "violet-400", desc: "Work-product issued" },
  { id: "archived", label: "ARCHIVED", accent: "slate-500", desc: "Closed / no-go" },
];

const ACCENT_BG = {
  "amber-400": "bg-amber-400",
  "emerald-400": "bg-emerald-400",
  "sky-400": "bg-sky-400",
  "violet-400": "bg-violet-400",
  "slate-500": "bg-slate-500",
};
const ACCENT_TEXT = {
  "amber-400": "text-amber-400",
  "emerald-400": "text-emerald-400",
  "sky-400": "text-sky-400",
  "violet-400": "text-violet-400",
  "slate-500": "text-slate-500",
};

export default function LeadsPipeline({ leads, onRefresh }) {
  const [draggingId, setDraggingId] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const [detail, setDetail] = useState(null);

  const grouped = STAGES.reduce((acc, s) => {
    acc[s.id] = leads.filter((l) => (l.status || "filed") === s.id);
    return acc;
  }, {});

  const moveLead = async (leadId, newStatus) => {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || lead.status === newStatus) return;
    try {
      await axios.patch(`${API}/admin/leads/${leadId}`, { status: newStatus });
      toast.success(`${lead.name} → ${newStatus.replace("_", " ").toUpperCase()}`);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Status update failed.");
    }
  };

  const onDragStart = (e, lead) => {
    setDraggingId(lead.id);
    e.dataTransfer.setData("text/plain", lead.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragEnd = () => {
    setDraggingId(null);
    setHoverStage(null);
  };
  const onDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (hoverStage !== stageId) setHoverStage(stageId);
  };
  const onDragLeave = (stageId) => {
    if (hoverStage === stageId) setHoverStage(null);
  };
  const onDrop = (e, stageId) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData("text/plain") || draggingId;
    setHoverStage(null);
    if (leadId) moveLead(leadId, stageId);
  };

  return (
    <div data-testid="leads-pipeline">
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-px bg-slate-800/70 border border-slate-800/70">
        {STAGES.map((stage) => {
          const items = grouped[stage.id] || [];
          const isHover = hoverStage === stage.id;
          return (
            <div
              key={stage.id}
              onDragOver={(e) => onDragOver(e, stage.id)}
              onDragLeave={() => onDragLeave(stage.id)}
              onDrop={(e) => onDrop(e, stage.id)}
              className={`bg-[#152138] min-h-[400px] flex flex-col transition-colors ${isHover ? "bg-[#1B2843]" : ""}`}
              data-testid={`pipeline-stage-${stage.id}`}
            >
              <div className="p-5 border-b border-slate-800/70 sticky top-0 bg-[#152138] z-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${ACCENT_BG[stage.accent]}`} />
                    <span className="mono-label text-white">{stage.label}</span>
                  </div>
                  <span className="mono-label text-slate-400">{items.length}</span>
                </div>
                <div className="mono-label text-slate-500 mt-2 text-[10px]">{stage.desc}</div>
              </div>

              <div className="p-3 space-y-2 flex-1">
                {items.length === 0 && (
                  <div className="mono-label text-slate-700 text-center py-8 border border-dashed border-slate-800/60">
                    {isHover ? "DROP TO MOVE" : "EMPTY"}
                  </div>
                )}
                {items.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    accent={stage.accent}
                    dragging={draggingId === lead.id}
                    onDragStart={(e) => onDragStart(e, lead)}
                    onDragEnd={onDragEnd}
                    onClick={() => setDetail(lead)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mono-label text-slate-500 mt-4">
        DRAG CARDS BETWEEN COLUMNS TO ADVANCE STATUS · CLICK A CARD FOR FULL DETAIL
      </p>

      {detail && <LeadDetailDrawer lead={detail} onClose={() => setDetail(null)} onRefresh={onRefresh} />}
    </div>
  );
}

function LeadCard({ lead, accent, dragging, onDragStart, onDragEnd, onClick }) {
  const purchases = lead.purchases || [];
  const totalPaid = purchases.reduce((s, p) => s + Number(p.amount || 0), 0);
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
      className={`group bg-[#0D1729] border border-slate-800/80 p-4 cursor-grab active:cursor-grabbing hover:border-slate-600 transition-colors ${dragging ? "opacity-40" : ""}`}
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-serif-display text-white text-base truncate">{lead.name}</div>
          <div className="mono-label text-slate-500 mt-1 truncate">{lead.firm || "INDIVIDUAL"}</div>
        </div>
        <GripVertical className="w-4 h-4 text-slate-700 group-hover:text-slate-500 shrink-0" />
      </div>

      <div className="mt-3 mono-label text-slate-400 text-[10px] line-clamp-1">
        {lead.case_type}
      </div>

      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {lead.asset_range && (
          <span className="mono-label bg-slate-800/60 px-2 py-1 text-[9px] text-slate-300">
            {lead.asset_range}
          </span>
        )}
        {totalPaid > 0 && (
          <span className={`mono-label px-2 py-1 text-[9px] ${ACCENT_TEXT[accent]} bg-slate-900 border border-slate-800`}>
            ● ${totalPaid.toLocaleString()}
          </span>
        )}
      </div>

      <div className="mt-3 mono-label text-slate-600 text-[9px]">
        {new Date(lead.created_at).toLocaleDateString()} · {lead.id.replace("lead_", "").slice(0, 8)}
      </div>
    </div>
  );
}

function LeadDetailDrawer({ lead, onClose, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const purchases = lead.purchases || [];
  const history = lead.status_history || [];

  const moveTo = async (status) => {
    setBusy(true);
    try {
      await axios.patch(`${API}/admin/leads/${lead.id}`, { status, note });
      toast.success(`Status → ${status.replace("_", " ").toUpperCase()}`);
      setNote("");
      onRefresh();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-detail-drawer">
      <button onClick={onClose} className="flex-1 bg-black/70 backdrop-blur-sm" aria-label="Close" />
      <div className="w-full max-w-xl bg-[#152138] border-l border-slate-800/70 overflow-y-auto">
        <div className="sticky top-0 bg-[#152138] border-b border-slate-800/70 p-6 flex items-center justify-between z-10">
          <div>
            <div className="mono-label">CASE FILE</div>
            <div className="font-serif-display text-2xl text-white mt-1">{lead.name}</div>
          </div>
          <button onClick={onClose} className="btn-secondary !py-2 !px-3" data-testid="drawer-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-8">
          <div className="grid grid-cols-2 gap-4">
            <DetailField icon={<Mail className="w-3 h-3" />} label="EMAIL" value={lead.email} mono />
            {lead.phone && <DetailField icon={<Phone className="w-3 h-3" />} label="PHONE" value={lead.phone} mono />}
            {lead.firm && <DetailField icon={<Briefcase className="w-3 h-3" />} label="FIRM" value={lead.firm} />}
            {lead.role && <DetailField label="ROLE" value={lead.role} />}
            {lead.jurisdiction && <DetailField icon={<Scale className="w-3 h-3" />} label="JURISDICTION" value={lead.jurisdiction} />}
            <DetailField label="CASE TYPE" value={lead.case_type} />
            {lead.asset_range && <DetailField label="ASSET RANGE" value={lead.asset_range} />}
            <DetailField label="FILED" value={new Date(lead.created_at).toLocaleString()} />
          </div>

          <div>
            <div className="mono-label mb-3">MATTER DESCRIPTION</div>
            <div className="text-slate-300 text-sm leading-relaxed bg-[#0D1729] border border-slate-800/80 p-4 whitespace-pre-wrap">
              {lead.description}
            </div>
          </div>

          {purchases.length > 0 && (
            <div>
              <div className="mono-label mb-3">PURCHASES · {purchases.length}</div>
              <div className="hairline">
                {purchases.map((p) => (
                  <div key={p.transaction_ref || `${p.provider}-${p.paid_at}`} className="p-4 border-b border-slate-800/60 last:border-b-0 flex items-center justify-between">
                    <div>
                      <div className="font-serif-display text-white">{p.service_name}</div>
                      <div className="mono-label text-slate-500 mt-1">
                        {p.provider?.toUpperCase()} · {new Date(p.paid_at).toLocaleString()}
                      </div>
                      <div className="mono-label text-slate-600 mt-1 text-[9px] break-all">{p.transaction_ref}</div>
                    </div>
                    <div className="font-serif-display text-2xl text-emerald-400">${Number(p.amount).toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="mono-label mb-3">PIPELINE · ADVANCE STATUS</div>
            <input
              type="text"
              className="input-line w-full"
              placeholder="Add an optional note for the audit trail…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="drawer-note-input"
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
              {STAGES.map((s) => (
                <button
                  key={s.id}
                  onClick={() => moveTo(s.id)}
                  disabled={busy || lead.status === s.id}
                  className={`btn-secondary !py-3 ${lead.status === s.id ? "!border-white !text-white" : ""}`}
                  data-testid={`drawer-move-${s.id}`}
                >
                  {lead.status === s.id ? `● ${s.label}` : `→ ${s.label}`}
                </button>
              ))}
            </div>
          </div>

          {history.length > 0 && (
            <div>
              <div className="mono-label mb-3">AUDIT TRAIL</div>
              <ol className="space-y-3">
                {[...history].reverse().map((h) => (
                  <li key={`${h.at}-${h.status}-${h.by}`} className="text-xs font-mono text-slate-400 border-l border-slate-700 pl-4 py-1">
                    <div className="text-white">{h.status?.toUpperCase()}</div>
                    <div className="text-slate-500 mt-1">
                      {new Date(h.at).toLocaleString()} · {h.by}
                    </div>
                    {h.note && <div className="text-slate-400 mt-1 italic">"{h.note}"</div>}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailField({ icon, label, value, mono }) {
  return (
    <div>
      <div className="mono-label flex items-center gap-1.5 mb-1.5 text-slate-500">
        {icon}
        {label}
      </div>
      <div className={`text-white ${mono ? "font-mono text-xs" : "text-sm"} break-words`}>{value || "—"}</div>
    </div>
  );
}
